import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { useCustomAuth } from "@/contexts/CustomAuthContext";
import { authApi } from '@/services/authService';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Rocket,
  ShieldCheck,
  BarChart3,
  Globe,
  Loader2,
  ArrowRight,
  Percent,
  TrendingUp,
  Info
} from "lucide-react";
import { toast } from "sonner";

export default function Afiliate() {
  useLanguage();
  const { user, refreshUser } = useCustomAuth();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);

  useEffect(() => {
    if (user) {
      if (user.nivel !== 'customer') {
        navigate('/');
      } else {
        setIsVerifying(false);
      }
    }
  }, [user, navigate]);

  if (isVerifying || !user) {
    return (
      <div className="h-screen w-full flex items-center justify-center bg-[#f8f9fa]">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
          <p className="text-xs font-medium text-slate-400 uppercase tracking-widest">{translateInterfaceText("A verificar conta...")}</p>
        </div>
      </div>
    );
  }

  const handleBecomeAfiliate = async () => {
    try {
      setIsSubmitting(true);

      const response = await authApi.post('/stripe/afiliate/finance/admin/upgrade-user', {
        targetUserId: user.id_user,
        newLevel: 'seller'
      });

      if (response.data && response.data.success) {
        toast.success(translateInterfaceText("Parabéns! A sua conta de afiliado foi ativada."));
        await refreshUser();
        navigate('/dashboard');
      } else {
        toast.error(response.data.message || translateInterfaceText("Não foi possível ativar o seu perfil de afiliado."));
      }
    } catch (error: any) {
      console.error(error);
      toast.error(error.response?.data?.message || translateInterfaceText("Ocorreu um erro ao processar a ativação."));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Layout>
      <div className="flex-1 lg:overflow-y-auto">
        <div className="p-8 max-w-[1200px] mx-auto space-y-12 text-slate-600 antialiased text-left">

          {/* HERO SECTION PRINCIPAL */}
          <section className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center pt-4">
            <div className="lg:col-span-7 space-y-5">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-50 text-blue-600 text-xs font-semibold">
                <Rocket size={12} />
                {translateInterfaceText("Programa de Afiliados Oficial")}</div>
              <h1 className="text-3xl lg:text-4xl font-extrabold tracking-tight text-slate-900 leading-tight">
                {translateInterfaceText("Recomende a nossa plataforma e ganhe")}{" "}<span className="text-blue-600">{translateInterfaceText("comissões recorrentes")}</span>
              </h1>
              <p className="text-sm text-slate-500 font-normal leading-relaxed max-w-xl">
                {translateInterfaceText("Ative a sua conta gratuitamente, partilhe o seu link exclusivo e construa uma nova fonte de rendimento acompanhando tudo em tempo real através de um painel analítico completo.")}</p>
              <div className="pt-2">
                <Button
                  onClick={handleBecomeAfiliate}
                  disabled={isSubmitting}
                  className="h-11 px-6 bg-blue-600 hover:bg-blue-700 text-white font-medium text-xs rounded-xl shadow-sm transition-colors flex items-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="animate-spin h-3.5 w-3.5" />
                      {translateInterfaceText("A processar...")}</>
                  ) : (
                    <>
                      {translateInterfaceText("Ativar Perfil Gratuitamente")}<ArrowRight size={14} />
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* CARD DE DESTAQUE RECORRENTE (Lado Direito do Hero) */}
            <div className="lg:col-span-5">
              <div className="bg-white rounded-2xl p-6 border border-slate-200 space-y-4 shadow-sm">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-blue-600">{translateInterfaceText("Ganhos Estimados")}</span>
                  <Percent size={16} className="text-blue-500" />
                </div>
                <div className="space-y-1">
                  <p className="text-[12px] font-medium text-slate-400">{translateInterfaceText("Comissão Recorrente")}</p>
                  <p className="text-3xl font-extrabold text-slate-900 tracking-tight"> {" "}{translateInterfaceText("De até 20% do valor do plano de assinatura do indicado.")}</p>
                </div>
                <div className="pt-2 border-t border-blue-100/60 flex items-center gap-2 text-xs text-slate-500">
                  <Info size={14} className="text-blue-400 shrink-0" />
                  <span>{translateInterfaceText("Receba todos os meses enquanto os seus indicados continuarem ativos na plataforma.")}</span>
                </div>
              </div>
            </div>
          </section>

          {/* COMO FUNCIONA - GRID DE VANTAGENS CLEAN */}
          <section className="space-y-6">
            <div className="space-y-0.5">
              <h2 className="text-lg font-bold text-slate-900">{translateInterfaceText("Como funciona o programa?")}</h2>
              <p className="text-[11px] text-slate-400 font-normal">{translateInterfaceText("Três passos simples para começar a faturar hoje mesmo.")}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
                <CardContent className="p-6 space-y-4">
                  <div className="h-9 w-9 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center shrink-0">
                    <Globe size={16} />
                  </div>
                  <div className="space-y-1">
                    <h3 className="text-xs font-bold text-slate-900">{translateInterfaceText("1. Pegue o seu link")}</h3>
                    <p className="text-[11px] text-slate-400 font-normal leading-relaxed">
                      {translateInterfaceText("Após a ativação, terá acesso instantâneo a um link parametrizado exclusivo para divulgar nas suas redes ou site.")}</p>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
                <CardContent className="p-6 space-y-4">
                  <div className="h-9 w-9 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center shrink-0">
                    <BarChart3 size={16} />
                  </div>
                  <div className="space-y-1">
                    <h3 className="text-xs font-bold text-slate-900">{translateInterfaceText("2. Acompanhe os cliques")}</h3>
                    <p className="text-[11px] text-slate-400 font-normal leading-relaxed">
                      {translateInterfaceText("Monitore todas as visitas, conversões e registos gerados através do seu tráfego em tempo real no nosso dashboard.")}</p>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
                <CardContent className="p-6 space-y-4">
                  <div className="h-9 w-9 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center shrink-0">
                    <TrendingUp size={16} />
                  </div>
                  <div className="space-y-1">
                    <h3 className="text-xs font-bold text-slate-900">{translateInterfaceText("3. Receba os pagamentos")}</h3>
                    <p className="text-[11px] text-slate-400 font-normal leading-relaxed">
                      {translateInterfaceText("Os seus saldos acumulados são liquidados diretamente na sua carteira configurada de forma segura e pontual.")}</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </section>

          {/* REGULAMENTO E REGRAS DE COMISSÃO */}
          <section className="bg-white border border-slate-200 rounded-2xl p-6 lg:p-8 space-y-6 shadow-sm">
            <div className="space-y-0.5">
              <h2 className="text-sm font-bold text-slate-900">{translateInterfaceText("Regras e Distribuição de Comissão")}</h2>
              <p className="text-[11px] text-slate-400 font-normal">{translateInterfaceText("Leia com atenção os termos operacionais de comissionamento.")}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-xs text-slate-600">
              <div className="space-y-4">
                <div className="flex items-start gap-3 p-3 bg-white rounded-xl border border-slate-200">
                  <div className="h-2 w-2 rounded-full bg-blue-500 mt-1.5 shrink-0" />
                  <div className="space-y-0.5">
                    <p className="font-bold text-slate-900">{translateInterfaceText("Atribuição por Último Clique")}</p>
                    <p className="text-slate-400 font-normal text-[11px]">{translateInterfaceText("A venda será contabilizada para o afiliado detentor do último link acessado pelo cliente antes do registo.")}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-3 bg-white rounded-xl border border-slate-200">
                  <div className="h-2 w-2 rounded-full bg-blue-500 mt-1.5 shrink-0" />
                  <div className="space-y-0.5">
                    <p className="font-bold text-slate-900">{translateInterfaceText("Prazo de Cookies de infinitos")}</p>
                    <p className="text-slate-400 font-normal text-[11px]">{translateInterfaceText("Seu link de indicação estará sempre ativo, nunca expira.")}</p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-start gap-3 p-3 bg-white rounded-xl border border-slate-200">
                  <div className="h-2 w-2 rounded-full bg-blue-500 mt-1.5 shrink-0" />
                  <div className="space-y-0.5">
                    <p className="font-bold text-slate-900">{translateInterfaceText("Retirada Mínima Permitida")}</p>
                    <p className="text-slate-400 font-normal text-[11px]">{translateInterfaceText("Poderá solicitar o resgate dos seus valores acumulados assim que atingir o piso mínimo estipulado no extrato.")}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-3 bg-white rounded-xl border border-slate-200">
                  <div className="h-2 w-2 rounded-full bg-blue-500 mt-1.5 shrink-0" />
                  <div className="space-y-0.5">
                    <p className="font-bold text-slate-900">{translateInterfaceText("Auditoria Anti-Fraude")}</p>
                    <p className="text-slate-400 font-normal text-[11px]">{translateInterfaceText("Auto-indicação ou práticas abusivas de SPAM resultarão no cancelamento imediato das comissões e da conta.")}</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* CTA FINAL DE ATIVAÇÃO DE CONTA */}
          <section className="text-center py-12 px-6 bg-slate-900 rounded-2xl text-white shadow-sm flex flex-col items-center space-y-4">
            <h2 className="text-xl font-bold tracking-tight">{translateInterfaceText("Pronto para começar a faturar?")}</h2>
            <p className="text-slate-400 max-w-md mx-auto text-xs font-normal leading-relaxed">
              {translateInterfaceText("Ao ativar a sua conta, ganhará acesso imediato ao seu painel administrativo para obter o seu link e começar a indicar.")}</p>
            <div className="pt-2 flex flex-col items-center gap-2.5">
              <Button
                onClick={handleBecomeAfiliate}
                disabled={isSubmitting}
                className="h-11 px-8 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-xl transition-colors shadow-sm"
              >
                {isSubmitting ? (
                  <Loader2 className="animate-spin h-4 w-4" />
                ) : (
                  translateInterfaceText("Ativar Minha Conta de Afiliado")
                )}
              </Button>
              <p className="text-[10px] text-slate-500 font-medium uppercase tracking-wider flex items-center gap-1.5">
                <ShieldCheck size={12} className="text-emerald-500" />
                {translateInterfaceText("Processamento seguro e imediato")}</p>
            </div>
          </section>

        </div>
      </div>
    </Layout>
  );
}